import React, { useEffect, useState } from 'react';
import { Loader2, Plus, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../../components/ui/select';
import { Product, ProductAlias, ProductOptions, ProductPayload } from '../../../types/stock.types';
import { productsApi } from '../../../utils/stock-api';

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    product: Product | null;
    options: ProductOptions;
    onSaved: () => void;
}

const EMPTY: ProductPayload = {
    sku: '',
    name: '',
    description: '',
    category: '',
    uom: '',
    weightKg: null,
    size: '',
    replacementValue: null,
};

/** Empty string -> null, so a cleared numeric field is stored as "unknown", not 0. */
const toNumberOrNull = (v: string): number | null => {
    const t = v.trim();
    if (!t) return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
};

export const ProductFormDialog: React.FC<Props> = ({
    open,
    onOpenChange,
    product,
    options,
    onSaved,
}) => {
    const [form, setForm] = useState<ProductPayload>(EMPTY);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    /**
     * Aliases are saved immediately against an existing product rather than batched with
     * the form: they have their own endpoints, and a half-typed alias should not block
     * saving the product itself.
     */
    const [aliases, setAliases] = useState<ProductAlias[]>([]);
    const [newAlias, setNewAlias] = useState('');
    const [aliasBusy, setAliasBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        setErrors({});
        setNewAlias('');

        if (product) {
            setForm({
                sku: product.sku,
                name: product.name,
                description: product.description ?? '',
                category: product.category,
                uom: product.uom,
                weightKg: product.weightKg,
                size: product.size ?? '',
                replacementValue: product.replacementValue,
            });
            setAliases(product.aliases ?? []);
        } else {
            setForm(EMPTY);
            setAliases([]);
        }
    }, [open, product]);

    const validate = () => {
        const next: Record<string, string> = {};
        if (!form.sku.trim()) next.sku = 'Product Code / SKU is required';
        if (!form.name.trim()) next.name = 'Product Name is required';
        if (!form.category) next.category = 'Category is required';
        if (!form.uom) next.uom = 'Unit of Measure is required';
        if (form.weightKg !== null && form.weightKg !== undefined && form.weightKg < 0) {
            next.weightKg = 'Weight cannot be negative';
        }
        if (form.replacementValue !== null && form.replacementValue !== undefined && form.replacementValue < 0) {
            next.replacementValue = 'Replacement value cannot be negative';
        }
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;

        const payload: ProductPayload = {
            ...form,
            sku: form.sku.trim(),
            name: form.name.trim(),
            description: form.description?.trim() || null,
            size: form.size?.trim() || null,
        };

        try {
            setSaving(true);
            if (product) {
                await productsApi.update(product.id, payload);
                toast.success('Product updated');
            } else {
                await productsApi.create(payload);
                toast.success('Product created');
            }
            onOpenChange(false);
            onSaved();
        } catch (err: any) {
            if (err?.field) setErrors({ [err.field]: err.message });
            toast.error(err?.message || 'Failed to save product');
        } finally {
            setSaving(false);
        }
    };

    const handleAddAlias = async () => {
        const alias = newAlias.trim();
        if (!alias || !product) return;
        try {
            setAliasBusy(true);
            const created = await productsApi.addAlias(product.id, alias);
            setAliases((prev) => [...prev, created]);
            setNewAlias('');
        } catch (err: any) {
            toast.error(err?.message || 'Failed to add alias');
        } finally {
            setAliasBusy(false);
        }
    };

    const handleRemoveAlias = async (alias: ProductAlias) => {
        if (!product) return;
        try {
            await productsApi.removeAlias(product.id, alias.id);
            setAliases((prev) => prev.filter((a) => a.id !== alias.id));
        } catch (err: any) {
            toast.error(err?.message || 'Failed to remove alias');
        }
    };

    const err = (field: string) =>
        errors[field] ? <p className="text-xs text-destructive mt-1">{errors[field]}</p> : null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[620px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{product ? 'Update Product' : 'Add Product'}</DialogTitle>
                    <DialogDescription>
                        Products are shared across every yard and every order type.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="sku">Product Code / SKU *</Label>
                            <Input
                                id="sku"
                                value={form.sku}
                                onChange={(e) => setForm({ ...form, sku: e.target.value })}
                                placeholder="LED-1124"
                            />
                            {err('sku')}
                        </div>
                        <div>
                            <Label htmlFor="size">Length / Size</Label>
                            <Input
                                id="size"
                                value={form.size ?? ''}
                                onChange={(e) => setForm({ ...form, size: e.target.value })}
                                placeholder="2.4m"
                            />
                        </div>
                    </div>

                    <div>
                        <Label htmlFor="name">Product Name *</Label>
                        <Input
                            id="name"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            placeholder="Ledger 2.4m"
                        />
                        {err('name')}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="category">Category *</Label>
                            <Select
                                value={form.category || undefined}
                                onValueChange={(v) => setForm({ ...form, category: v })}
                            >
                                <SelectTrigger id="category">
                                    <SelectValue placeholder="Select a category" />
                                </SelectTrigger>
                                <SelectContent>
                                    {options.categories.map((c) => (
                                        <SelectItem key={c} value={c}>{c}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {err('category')}
                        </div>
                        <div>
                            <Label htmlFor="uom">Unit of Measure *</Label>
                            <Select
                                value={form.uom || undefined}
                                onValueChange={(v) => setForm({ ...form, uom: v })}
                            >
                                <SelectTrigger id="uom">
                                    <SelectValue placeholder="Select a unit" />
                                </SelectTrigger>
                                <SelectContent>
                                    {options.uoms.map((u) => (
                                        <SelectItem key={u} value={u}>{u}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {err('uom')}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="weightKg">Weight (kg)</Label>
                            <Input
                                id="weightKg"
                                type="number"
                                step="0.001"
                                min="0"
                                value={form.weightKg ?? ''}
                                onChange={(e) => setForm({ ...form, weightKg: toNumberOrNull(e.target.value) })}
                            />
                            <p className="text-xs text-muted-foreground mt-1">Per unit — used for load calculations.</p>
                            {err('weightKg')}
                        </div>
                        <div>
                            <Label htmlFor="replacementValue">Replacement Value</Label>
                            <Input
                                id="replacementValue"
                                type="number"
                                step="0.01"
                                min="0"
                                value={form.replacementValue ?? ''}
                                onChange={(e) => setForm({ ...form, replacementValue: toNumberOrNull(e.target.value) })}
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                Puts a dollar figure on shortfalls and damage.
                            </p>
                            {err('replacementValue')}
                        </div>
                    </div>

                    <div>
                        <Label htmlFor="description">Description</Label>
                        <Textarea
                            id="description"
                            value={form.description ?? ''}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            rows={2}
                        />
                    </div>

                    {/* Aliases — only once the product exists to attach them to. */}
                    <div className="border-t border-border pt-4">
                        <Label className="flex items-center gap-1.5">
                            <Sparkles size={14} className="text-muted-foreground" />
                            Aliases
                        </Label>
                        <p className="text-xs text-muted-foreground mt-1 mb-2">
                            Alternate names the AI parser and product search match against — the yard
                            says "8ft ledger", the catalogue says {form.sku || 'LED-1124'}.
                        </p>

                        {!product ? (
                            <p className="text-xs text-muted-foreground italic">
                                Save the product first, then add aliases.
                            </p>
                        ) : (
                            <>
                                <div className="flex flex-wrap gap-2 mb-2">
                                    {aliases.length === 0 && (
                                        <span className="text-xs text-muted-foreground">No aliases yet.</span>
                                    )}
                                    {aliases.map((a) => (
                                        <span
                                            key={a.id}
                                            className="inline-flex items-center gap-1 text-xs font-medium text-primary bg-accent px-2 py-1 rounded-md"
                                        >
                                            {a.alias}
                                            {a.autoGenerated && (
                                                <Sparkles size={10} aria-label="Learned from a corrected AI match" />
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveAlias(a)}
                                                aria-label={`Remove alias ${a.alias}`}
                                                className="hover:text-destructive"
                                            >
                                                <X size={12} />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                                <div className="flex gap-2">
                                    <Input
                                        value={newAlias}
                                        onChange={(e) => setNewAlias(e.target.value)}
                                        placeholder="Add an alias..."
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') { e.preventDefault(); handleAddAlias(); }
                                        }}
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={handleAddAlias}
                                        disabled={!newAlias.trim() || aliasBusy}
                                    >
                                        <Plus size={16} />
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={saving}>
                            {saving && <Loader2 size={16} className="animate-spin mr-2" />}
                            {product ? 'Save Changes' : 'Add Product'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default ProductFormDialog;
